'use client'
import { getMessaging, getToken } from 'firebase/messaging'
import { useState, useCallback } from 'react'

import postFcmToken from '../actions/notifications/post-fcm-token'
import firebaseApp from '../configs/firebase'
import getLastCharacters from '../helpers/get-last-characters'
import { setFcmToken } from '../store/slice/ui-slice'

import { useAppDispatch } from './use-app-dispatch'

const useFcmToken = () => {
  const dispatch = useAppDispatch()
  const [notificationPermissionStatus, setNotificationPermissionStatus] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default',
  )
  const [loading, setLoading] = useState(false)

  const requestFcmToken = useCallback(async (retryCount = 0) => {
    try {
      setLoading(true)
      const messaging = getMessaging(firebaseApp)
      const currentToken = await getToken(messaging)

      if (!currentToken) {
        throw new Error('No registration token available.')
      }

      dispatch(setFcmToken(currentToken))
      const res = await postFcmToken(currentToken)

      if (!res?.success) {
        throw new Error(
          `Error while registering token (Trace ID : ${
            res?.traceId ? getLastCharacters(res?.traceId) : 'unknown traceId'
          })`,
        )
      }

      setLoading(false)
      return 'Token retrieved successfully.'
    } catch (error) {
      if (retryCount < 3) {
        await new Promise((resolve) => setTimeout(resolve, 1000)) // Add a delay before retrying
        return requestFcmToken(retryCount + 1)
      }

      setLoading(false)

      throw error
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const requestNotificationPermission = useCallback(async () => {
    if (
      typeof window === 'undefined' ||
      !('serviceWorker' in navigator) ||
      typeof Notification === 'undefined'
    ) {
      return Promise.reject(new Error('Service Worker Unsupported.'))
    }

    try {
      if ('safari' in window && notificationPermissionStatus === 'granted') {
        await requestFcmToken()
        return 'Notification permission granted and token requested.'
      }

      const permission = await Notification.requestPermission()

      setNotificationPermissionStatus(permission)

      if (permission === 'granted') {
        await requestFcmToken()
        return 'Notification permission granted and token requested.'
      } else if (permission === 'denied') {
        throw new Error(
          'Your browser block notification, enable notifications in your browser settings.',
        )
      }
    } catch (error) {
      if (error) {
        throw error
      }
    }
  }, [requestFcmToken])

  return { notificationPermissionStatus, requestNotificationPermission, loading }
}

export default useFcmToken
